import Cookies from "js-cookie";
import {
  cookies,
  cookieTypes,
  localStorageVars,
  sessionStorageVars
} from "./CookieVariables";

// Returns a list of cookie types with their properties
export function getCookieTypes() {
  return cookieTypes;
}

function formatAndFilter(items, typeKey, params) {
  const formattedItems = items.map((item) => ({
    ...item,
    [typeKey]: true,
    type:
      typeKey === "isCookie"
        ? "cookie"
        : typeKey === "isLocalStorage"
          ? "localStorage"
          : "sessionStorage",
    isEssential: item.type === "essential",
    isAnalytical: item.type === "analytical",
    isPerformance: item.type === "performance",
    isInformational: item.type === "informational",
    domain: item?.domain || "bestcoastpairings.com"
  }));

  if (params) {
    return formattedItems.filter((item) =>
      Object.keys(params).some((key) => item[key] === params[key])
    );
  }

  return formattedItems;
}

// Returns a list of cookies with their properties
function getCookies(params) {
  return formatAndFilter(cookies, "isCookie", params);
}

// Returns a list of local storage variables with their properties
function getLocalStorageVars(params) {
  return formatAndFilter(localStorageVars, "isLocalStorage", params);
}

// Returns a list of session storage variables with their properties
function getSessionStorageVars(params) {
  return formatAndFilter(sessionStorageVars, "isSession", params);
}

// Returns a list of all cookies, local storage variables, and session storage variables
export function getAllCookies(params) {
  return [
    ...getCookies(params),
    ...getLocalStorageVars(params),
    ...getSessionStorageVars(params)
  ];
}

// Retrieves a specific cookie by name from the list of all cookies
function _getCookie(name) {
  const allCookies = getAllCookies();
  return allCookies.find((cookie) => cookie.name === name) || undefined;
}

export function hasCookiePermission(cookieName, authContext) {
  const cookie = _getCookie(cookieName);

  const storedConsent = Cookies.get("CookieConsent");
  const cookieConsent = authContext?.authState?.cookieConsent
    ? authContext.authState.cookieConsent
    : storedConsent
      ? JSON.parse(storedConsent)
      : {
          essential: true,
          analytical: false,
          performance: false,
          informational: false
        };

  if (!cookie) {
    return false;
  }
  if (cookie.purpose === "essential") {
    return true;
  }

  if (cookieConsent) {
    return Boolean(cookieConsent[cookie.purpose]);
  }
}

// Sets local storage, session storage, or cookies based on the provided name, value, and user's consent levels
export function setLocalStorage(name, value, authContext) {
  if (!name || !value) {
    console.warn("setLocalStorage called without name or value");
    return;
  }

  const targetName = name?.endsWith("Rows") ? "[]Rows" : name;

  const localVar = _getCookie(targetName);
  const hasPermission = hasCookiePermission(targetName, authContext);

  if (localVar && hasPermission) {
    localStorage?.setItem(
      name,
      typeof value === "object" ? JSON.stringify(value) : value
    );
  }
}

export function setSessionStorage(name, value, authContext) {
  const sessionVar = _getCookie(name);
  const hasPermission = hasCookiePermission(name, authContext);

  if (sessionVar && hasPermission) {
    sessionStorage?.setItem(
      name,
      typeof value === "object" ? JSON.stringify(value) : value
    );
  }
}

export function setCookie(name, value, authContext) {
  const cookie = _getCookie(name);
  const hasPermission = hasCookiePermission(name, authContext);

  if (cookie && hasPermission) {
    const cookieParams = {};
    const formattedValue =
      typeof value === "object" ? JSON.stringify(value) : value;

    if (cookie.expiration) {
      const expiration = cookie.expiration;
      if (expiration === "End of session" || expiration === "On logout") {
        // If the cookie is set to expire at the end of the session or on logout, no need to set an expiration date
      } else if (expiration === "Does not expire") {
        cookieParams.expires = 365 * 10; // 10 years
      } else {
        const days = parseInt(expiration.split(" ")[0]);
        cookieParams.expires = days;
      }
    }

    if (Object.keys(cookieParams).length > 0) {
      Cookies.set(name, formattedValue, cookieParams);
    } else {
      Cookies.set(name, formattedValue);
    }
  }
}

// Helper function that retrieves the value of local storage, session storage, or cookies based on the provided name and user's consent levels
function getStorageValue(name, authContext, getItemFunction) {
  if (!name) {
    console.warn("getStorageValue called without name or value");
    return undefined;
  }

  const targetName = name?.endsWith("Rows") ? "[]Rows" : name;

  const storageVar = _getCookie(targetName);
  const hasPermission = hasCookiePermission(targetName, authContext);

  if (!storageVar || !hasPermission) return undefined;

  let value = getItemFunction(name);

  return value || undefined;
}

export function getLocalStorage(name, authContext) {
  return getStorageValue(name, authContext, (key) =>
    localStorage?.getItem(key)
  );
}

export function getSessionStorage(name, authContext) {
  return getStorageValue(name, authContext, (key) =>
    sessionStorage?.getItem(key)
  );
}

export function getCookie(name, authContext) {
  return getStorageValue(name, authContext, (key) => Cookies.get(key));
}

export function removeLocalStorage(name) {
  localStorage?.removeItem(name);
}

export function removeSessionStorage(name) {
  sessionStorage?.removeItem(name);
}

export function removeCookie(name) {
  Cookies.remove(name);
}
